import type {IAnalytics} from '@sail/observability';
import {getStripeOrGraphQLErrorMetadata} from '@stripe-internal/connect-embedded-lib';
import type {AccountSessionClaim} from '../data-layer-frame/types';
import type {IDeferredPromise} from '../utils/DeferredPromise';
import type {IFrameMessenger} from '../data-layer-client/FrameMessenger';
import {getDevLogger} from '../utils/getLogger';
import type {
  RiskSignalsCollectionHandler,
  RiskSignalsCollectionEvent,
  LoadError,
  RiskSignalCollectionErrorType,
} from './ConnectJSInterface/RiskSignalCollectionTypes';
import {mapAuthErrorToEmbeddedError} from './utils/embeddedLoadErrors';
import type {MetaOptions} from './ConnectJSInterface/InitAndUpdateOptionsTypes';
import type {ReducedMetaOptions} from './ConnectJsOptions';
import {withErrorReportingAndAnalytics} from '../data-layer-client/ErrorReportingAndAnalytics';

/**
 * Collects risk signals based on the following conditions:
 * - account_onboarding component is enabled
 * - Either Managed Risk for Banks Beta flag OR Managed Risk for Custom flag is enabled
 * - `risk_signals.session_metadata` requirement exists
 *
 * @param deferredAuthPromise - The promise that resolves to the account session claim.
 * @param frameMessenger - The frame messenger for data layer communication.
 * @param analytics - Analytics instance for tracking metrics.
 * @param metaOptions - Meta options for analytics configuration.
 * @param onRiskSignalsCollection - Optional callback for risk signals collection events.
 * @returns Promise<void>
 */
export async function maybeCollectRiskSignals(
  deferredAuthPromise: IDeferredPromise<AccountSessionClaim>,
  frameMessenger: IFrameMessenger,
  analytics: IAnalytics,
  metaOptions: MetaOptions | ReducedMetaOptions,
  onRiskSignalsCollection?: RiskSignalsCollectionHandler,
): Promise<void> {
  try {
    const authResult = await deferredAuthPromise.promise;

    const {shouldSkip, reason} = shouldSkipRiskSignalsCollection(authResult);

    if (shouldSkip) {
      getDevLogger().info(
        'maybeCollectRiskSignals: Risk signals collection skipped',
        {reason},
      );

      // Call callback with skipped status
      safelyInvokeCallback(onRiskSignalsCollection, {
        status: 'skipped',
        skippedReason: reason,
      });

      return;
    }

    await collectRiskSignals(
      frameMessenger,
      analytics,
      metaOptions,
      onRiskSignalsCollection,
    );
  } catch (error) {
    // Auth failure before risk signal collection
    getDevLogger().error(
      'maybeCollectRiskSignals: Authentication failed',
      error,
    );

    // Reuse the same loadError mapper in embedded components to map auth error to load_error type
    // Use casting to exclude `render_error` as it is not applicable to this path (no component rendering)
    const loadError = mapAuthErrorToEmbeddedError(error) as LoadError;

    safelyInvokeCallback(onRiskSignalsCollection, {
      status: 'failed',
      error: {
        type: 'load_error',
        loadError,
      },
    });
  }
}

/**
 * Collects risk signals via data layer.
 */
async function collectRiskSignals(
  frameMessenger: IFrameMessenger,
  analytics: IAnalytics,
  metaOptions: MetaOptions | ReducedMetaOptions,
  onRiskSignalsCollection?: RiskSignalsCollectionHandler,
): Promise<void> {
  getDevLogger().info('maybeCollectRiskSignals: collectRiskSignals called');

  try {
    const result = await withErrorReportingAndAnalytics(
      null,
      metaOptions,
      analytics,
      'submerchant_surfaces_collect_risk_signals',
      frameMessenger.collectRiskSignals,
      undefined,
    );

    getDevLogger().info(
      'maybeCollectRiskSignals: Risk signals collection result',
      result,
    );

    // Invoke callback with the result
    safelyInvokeCallback(onRiskSignalsCollection, result);
  } catch (error) {
    // Map error to callback format
    const mappedError = mapToRiskSignalCollectionError(error);

    safelyInvokeCallback(onRiskSignalsCollection, {
      status: 'failed',
      error: mappedError,
    });
  }
}

/**
 * Checks if risk signals collection should be skipped.
 * @param authResult - The account session claim.
 * @returns {shouldSkip: boolean, reason?: RiskSignalCollectionSkippedReason} - Whether to skip risk signals collection and the reason.
 */
function shouldSkipRiskSignalsCollection(authResult: AccountSessionClaim):
  | {
      shouldSkip: true;
      reason: 'required_component_not_enabled' | 'not_required';
    }
  | {
      shouldSkip: false;
      reason?: undefined;
    } {
  // Check if account_onboarding component is enabled
  const isAccountOnboardingEnabled =
    !!authResult.components?.account_onboarding?.enabled;

  if (!isAccountOnboardingEnabled) {
    return {shouldSkip: true, reason: 'required_component_not_enabled'};
  }

  // Check if either Managed Risk for Banks Beta OR Managed Risk for Custom flag is enabled
  const isManagedRiskForBanksBetaEnabled =
    !!authResult.flags?.managed_risk_for_banks_beta;
  const isManagedRiskForCustomEnabled =
    !!authResult.flags
      ?.enable_stripe_owns_payments_losses_and_platform_owns_requirement_collection_accounts;

  if (!isManagedRiskForBanksBetaEnabled && !isManagedRiskForCustomEnabled) {
    return {shouldSkip: true, reason: 'not_required'};
  }

  return {shouldSkip: false};
}

/**
 * Maps errors from data layer to RiskSignalCollectionError format for callbacks.
 */
function mapToRiskSignalCollectionError(error: unknown): {
  type: RiskSignalCollectionErrorType;
  message?: string;
} {
  const e = getStripeOrGraphQLErrorMetadata(error);

  if (e.stripeErrorCode === 'Failed to fetch') {
    return {type: 'api_connection_error', message: e.stripeErrorMessage};
  }
  if (e.stripeStatusCode === 429) {
    return {type: 'rate_limit_error', message: e.stripeErrorMessage};
  }
  if (e.stripeStatusCodeRange === '4xx') {
    return {type: 'invalid_request_error', message: e.stripeErrorMessage};
  }
  return {type: 'api_error', message: e.stripeErrorMessage};
}

/**
 * Safely invokes the onRiskSignalsCollection callback, catching and logging any errors
 * to prevent client callback errors from disrupting the risk signals collection flow.
 *
 * @param callback - The optional callback to invoke
 * @param event - The event to pass to the callback
 */
function safelyInvokeCallback(
  callback: RiskSignalsCollectionHandler | undefined,
  event: RiskSignalsCollectionEvent,
): void {
  if (!callback) {
    return;
  }

  try {
    getDevLogger().info(
      'maybeCollectRiskSignals: Invoking onRiskSignalsCollection callback',
      event,
    );
    callback(event);
  } catch (callbackError) {
    getDevLogger().error(
      'maybeCollectRiskSignals: Error in onRiskSignalsCollection callback',
      event,
      callbackError,
    );
  }
}
